import { useCallback } from "react";
import { proxy } from "valtio";

import { waitForPendingUploadsAsync } from "../components/dialogs/pending-uploads-dialog";
import { useAppContext } from "./useAppContext";
import { useErrorHandling } from "./useErrorHandling";

// Actually we don't really need a valtio state,
// as we don't need it in a render function (= rerender on change), only used in callbacks
const navigationState = proxy<{
	allowNavigateAway: boolean;
}>({
	/** Indicates if it is OK to leave the page without the 'Are you sure to leave' message at 'unload' event */
	allowNavigateAway: false,
});

export function useNavigateToUrl() {
	const { editor, logger } = useAppContext();
	const { createErrorHandler } = useErrorHandling();

	return useCallback(
		(
			// Don't use an async function, because else callers must handle errors
			url: string,
			options: {
				waitForPendingUploads?: "all" | "onlyUsed" | "none";
				backButtonClicked?: boolean;
			} = {},
		) => {
			const { waitForPendingUploads, backButtonClicked } = options;
			if (url) {
				// Wait for uploads before save, returns false if cancelled
				(waitForPendingUploads !== "none"
					? waitForPendingUploadsAsync(
							{
								getHasPendingUploads: () =>
									editor.hasPendingUploads(waitForPendingUploads === "onlyUsed"),
							},
							{ flow: "leavePage" },
					  )
					: Promise.resolve(true)
				)
					// Cancel redirect
					.then((hasFinishedUploading) => {
						// Is waiting cancelled?
						if (!hasFinishedUploading) return;

						// Validated, on unload message needed
						if (!backButtonClicked) {
							navigationState.allowNavigateAway = true;
						}

						logger.warn(`Navigating to URL: ${url}`);

						if (url) {
							window.location.assign(url);
						}
					})
					.catch(createErrorHandler());
			}
		},
		[createErrorHandler, editor, logger],
	);
}

/** This is used for the browser unload event */
export function useGetIsNavigateAwayAllowed() {
	return useCallback(() => navigationState.allowNavigateAway, []);
}
